import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { ChangeEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2 } from 'lucide-react';
import { router } from '@inertiajs/react';
import type { PatientCreatedEventDetail } from '@/lib/patientEvents';
import { patientFlagsToArray } from './types';
import { logError } from '@/lib/logger';
import { TemporaryPasswordPanel } from './TemporaryPasswordPanel';

export type NewPatientModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: React.ReactNode;
  onCreated?: (patient: PatientCreatedEventDetail['patient'], temporaryPassword: string) => void;
};

type FormStatus = 'idle' | 'submitting' | 'success';

type FormState = {
  first_name: string;
  last_name: string;
  email: string;
  prompt: string;
  profile: string;
};

const EMPTY_FORM: FormState = {
  first_name: '',
  last_name: '',
  email: '',
  prompt: '',
  profile: '',
};

export function NewPatientModal({ open, onOpenChange, trigger, onCreated }: NewPatientModalProps) {
  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [formErrors, setFormErrors] = useState<string[] | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [createdPatient, setCreatedPatient] = useState<PatientCreatedEventDetail['patient'] | null>(
    null,
  );
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const resetForm = useCallback(() => {
    setFormData(EMPTY_FORM);
    setFormStatus('idle');
    setFormErrors(null);
    setTemporaryPassword('');
    setCreatedPatient(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  useEffect(() => {
    if (open && formStatus !== 'success') {
      setTimeout(() => firstFieldRef.current?.focus(), 0);
    }
  }, [open, formStatus]);

  const handleInputChange = (field: keyof FormState, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors) {
      setFormErrors(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formStatus === 'submitting') return;

    setFormStatus('submitting');
    setFormErrors(null);
    setCreatedPatient(null);
    setTemporaryPassword('');

    try {
      const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
        ?.content;
      const response = await fetch('/therapists/patients.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'same-origin',
        body: JSON.stringify({ patient: formData }),
      });

      if (response.ok) {
        const data = (await response.json()) as {
          patient: PatientCreatedEventDetail['patient'];
          temporary_password: string;
        };
        setCreatedPatient({
          ...data.patient,
          flags: patientFlagsToArray(data.patient.flags),
        });
        setTemporaryPassword(data.temporary_password || '');
        setFormStatus('success');
        onCreated?.(data.patient, data.temporary_password || '');
      } else if (response.status === 422) {
        const data = await response.json();
        const fallback =
          data.error === 'subscription_limit_reached'
            ? "Limite clienti raggiunto. Contatta l'amministrazione."
            : data.error === 'subscription_inactive'
              ? "Nessun abbonamento operativo. Contatta l'amministrazione."
              : 'Errore durante la creazione del cliente';
        setFormErrors(data.errors || [data.message || fallback]);
        setFormStatus('idle');
      } else {
        setFormErrors(['Errore inatteso durante la creazione del cliente. Riprova.']);
        setFormStatus('idle');
      }
    } catch (error) {
      logError('Errore creazione cliente', error);
      setFormErrors(['Impossibile comunicare col server. Verifica la connessione e riprova.']);
      setFormStatus('idle');
    }
  };

  const handleViewPatient = () => {
    const patientId = createdPatient?.id;
    if (!patientId) return;
    onOpenChange(false);
    resetForm();
    router.visit(`/therapists/patients/${patientId}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? (
        <DialogTrigger asChild>
          {React.isValidElement(trigger) ? trigger : <span>{trigger}</span>}
        </DialogTrigger>
      ) : null}
      <DialogContent
        className="max-w-[425px] md:max-w-2xl"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          firstFieldRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Nuovo cliente</DialogTitle>
          <DialogDescription>
            Compila i dati minimi del cliente. La password temporanea verrà generata
            automaticamente.
          </DialogDescription>
        </DialogHeader>
        {formStatus !== 'success' ? (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="np-first_name">Nome *</Label>
                <Input
                  id="np-first_name"
                  value={formData.first_name}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('first_name', e.target.value)
                  }
                  placeholder="Mario"
                  required
                  disabled={formStatus === 'submitting'}
                  ref={firstFieldRef}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="np-last_name">Cognome *</Label>
                <Input
                  id="np-last_name"
                  value={formData.last_name}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('last_name', e.target.value)
                  }
                  placeholder="Rossi"
                  required
                  disabled={formStatus === 'submitting'}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="np-email">Email *</Label>
                <Input
                  id="np-email"
                  type="email"
                  value={formData.email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('email', e.target.value)
                  }
                  placeholder="mario.rossi@example.com"
                  required
                  disabled={formStatus === 'submitting'}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="np-profile">Profilo clinico del cliente</Label>
                <Textarea
                  id="np-profile"
                  value={formData.profile}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    handleInputChange('profile', e.target.value)
                  }
                  className="min-h-[100px]"
                  placeholder="Descrizione del profilo clinico del cliente"
                  disabled={formStatus === 'submitting'}
                />
                <p className="text-xs text-muted-foreground">
                  Puoi aggiornare questo dettaglio anche successivamente dalla scheda del cliente.
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="np-prompt">Istruzioni per l&apos;assistente (opzionale)</Label>
                <Textarea
                  id="np-prompt"
                  value={formData.prompt}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    handleInputChange('prompt', e.target.value)
                  }
                  className="min-h-[100px]"
                  placeholder="Note utili per personalizzare l'assistente del cliente"
                  disabled={formStatus === 'submitting'}
                />
                <p className="text-xs text-muted-foreground">
                  Puoi aggiornare queste istruzioni in qualsiasi momento dalla scheda del cliente.
                </p>
              </div>
            </div>
            {formErrors && formErrors.length > 0 && (
              <div className="text-sm text-destructive space-y-1" role="alert">
                {formErrors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            )}
            <DialogFooter className="gap-2">
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    resetForm();
                  }}
                  disabled={formStatus === 'submitting'}
                >
                  Annulla
                </Button>
              </DialogClose>
              <Button type="submit" disabled={formStatus === 'submitting'}>
                {formStatus === 'submitting' ? 'Salvataggio...' : 'Salva cliente'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4 pt-4">
            <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              <span>Cliente creato con successo.</span>
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">Dettagli cliente</h3>
              <div className="rounded-md border bg-background px-3 py-3 text-sm space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Nome</span>
                  <span className="font-medium">
                    {createdPatient?.display_name ||
                      [createdPatient?.first_name, createdPatient?.last_name]
                        .filter(Boolean)
                        .join(' ') ||
                      createdPatient?.email}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium">{createdPatient?.email}</span>
                </div>
                {createdPatient?.prompt && createdPatient.prompt.trim().length > 0 && (
                  <div>
                    <span className="text-muted-foreground block">Istruzioni iniziali</span>
                    <p className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap text-sm text-foreground/90">
                      {createdPatient.prompt}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <TemporaryPasswordPanel
                password={temporaryPassword}
                helperText="Conserva questa password e comunicala al cliente. Sarà richiesta solo al primo accesso."
                autoFocusCopyButton
              />
            </div>
            <DialogFooter className="gap-2">
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    resetForm();
                    onOpenChange(false);
                  }}
                >
                  Chiudi
                </Button>
              </DialogClose>
              <Button type="button" onClick={handleViewPatient} disabled={!createdPatient}>
                Vai alla scheda cliente
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default NewPatientModal;
